/**
 * Admin recover-order — what an operator posts to
 * `POST /api/admin/partial-payments/[id]/recover-order` to force-create a
 * persisted `orders` row for a money-captured-but-order-missing
 * `partial_payment_alerts` event (cron-detected orphan, Stripe outage replay,
 * etc.).
 *
 * Every body field is optional on the wire — the route fans out a chain of
 * fallbacks (request body → alert metadata → PayPal `payer.email` lookup → DB
 * cart rows → listing prices) and only fails if it can't compose a buyer +
 * items + currency from somewhere. In normal admin UX the frontend posts only
 * `{ dryRun: boolean }` and lets the route discover everything; the other
 * fields exist for last-resort manual override paths (e.g. cart was cleared,
 * email mis-resolves, custom currency).
 *
 * NOT mobile-consumed — this route is admin-gated upstream (`requireAdmin()`),
 * so the schema can tighten freely without freeze constraints.
 *
 * @see app/api/admin/partial-payments/[id]/recover-order/route.ts — handler
 * @see .tondocs/stripe/INCIDENT_2026_05_08_RECOVERY_PLAN.md — operator playbook
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * Loose shipping-info override. Mirrors the field names the route reads off
 * `body.shippingInfo` (line 324). Every field is optional because the route
 * falls back to PayPal `payer.shipping.address` when omitted; pass only the
 * fields the operator wants to override.
 */
export interface AdminRecoverOrderShippingInfo {
  address?: string;
  apartment?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  phoneCountryCode?: string;
  phoneNumber?: string;
}

/**
 * Manual item override. Used only on the fallback branch when the buyer's cart
 * is empty and the operator must hand-roll the order lines from the original
 * PayPal capture.
 */
export interface AdminRecoverOrderItem {
  listingId: string;
  quantity: number;
  price?: number;
}

export interface AdminRecoverOrderRequest {
  /**
   * Buyer's `auth.users.id` — used as `orders.user_id`. Optional: the route
   * falls back to `alert.user_id`, then to a PayPal-payer-email lookup against
   * `profiles.email`. Provide only when those fallbacks fail (e.g. payer email
   * doesn't match any profile, or the alert lost its user_id).
   */
  userId?: string;
  /** Fallback seller-id list if the alert and PayPal fetch both fail. */
  sellerIds?: string[];
  /** Manual item override when the buyer's cart was cleared post-capture. */
  items?: AdminRecoverOrderItem[];
  /** Address override (merged over PayPal-derived defaults). */
  shippingInfo?: AdminRecoverOrderShippingInfo;
  /** ISO-4217 currency override (e.g. "USD"). Falls back to listing currency. */
  currency?: string;
  /**
   * If true, the route returns a preview JSON (totals, sellers, warnings,
   * priceDrifts) without executing the atomic order INSERT. Used by the
   * admin UI to show the operator what the recovery will produce.
   */
  dryRun?: boolean;
}

// ---------------------------------------------------------------------------
// Runtime parse — Zod schema (boundary)
// ---------------------------------------------------------------------------

const AdminRecoverOrderShippingInfoSchema = z.object({
  address: z.string().optional(),
  apartment: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  country: z.string().optional(),
  email: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  phoneCountryCode: z.string().optional(),
  phoneNumber: z.string().optional(),
});

const AdminRecoverOrderItemSchema = z.object({
  listingId: z.string().min(1),
  quantity: z.number().int().positive(),
  price: z.number().optional(),
});

export const AdminRecoverOrderRequestSchema = pinned<AdminRecoverOrderRequest>()(
  z.object({
    userId: z.string().min(1).optional(),
    sellerIds: z.array(z.string().min(1)).optional(),
    items: z.array(AdminRecoverOrderItemSchema).optional(),
    shippingInfo: AdminRecoverOrderShippingInfoSchema.optional(),
    currency: z.string().min(1).optional(),
    dryRun: z.boolean().optional(),
  }),
);
